const FORCE_RELOAD_CHECK_TIME = 60_000;
const IDLE_TIME = 1000 * 60 * 60; // 60 MIN  IDEAL TIME OUT
const KEY = 'key';
const POS_ID = 'pos_id';
const POS_NAME = 'pos_name';
const DUMMY_ITEM = 'dummy_item';
const ID = 'id';
const MAX_CART_ITEM_QUANTITY = 19;
const RESET_HEALTH_STATUS_ONCLOSE_DELAY = 1000;
const TASKROUTER_WSLINK = 'TASK_CHANNEL';
const TASKROUTER_RECONNECT_ATTEMPTS = 5;
const TASKROUTER_RECONNECT_INTERVAL = 5000;
const TIMEOUT = 5000;
const ORDER_STATUS_ICON_RESET_INTERVAL = 3000;
const SHOW_AI_ESCALATION_INTERVAL = 5000;
const OUT_OF_SESSION = 'out of session';
const AUTH_TOKEN_RESPONSE_HEADER = 'x-presto-auth-token';
const AGENT_INTERCEPTION_CONFIRM_CONTENT =
  'AI is ON: Would you like to intercept/take over this order?';
// Interactions that are NOT "taking over the order", so they must not raise the
// agent-interception prompt: the header, the account menu, the Layout-preference
// dialog, the pane resize handles (some render in portals, outside the header
// DOM), the transcript, and the task-router advisory banner.
//
// The transcript is supervisory, not an order action: reading back, scrolling to
// the live edge, and reporting mis-heard ASR all leave the order untouched. The
// marker covers the message stream, the "New messages" pill, and the ASR-report
// confirm dialog — the dialog needs its own tag because it renders in a portal,
// outside the stream's DOM.
//
// The task-router banner is the strongest case of all: it exists to tell the
// agent NOT to touch this order, so acknowledging it must never be read as
// taking the order over.
//
// Issue reporting (HITL-1499) is supervisory too: flagging a defect — or undoing
// the flag — says nothing about who is driving the order. The marker sits on the
// Issue button's group wrapper and on the undo toast's root, so it covers the
// icon, the keycap and the Undo button alike.
//
// Shared by both guards — AgentInterceptionConfirm (hitl_3_0) and AIStatusBar
// (legacy) — which listen on document in the capture phase and so cannot be
// stopped by any handler on the element itself. They previously each held their
// own copy of this list and were required to stay in lockstep by hand.
/**
 * The issue-reporting opt-out marker, as a selector.
 *
 * Shared by the agent-interception guards below AND by the omnibar's refocus guard
 * (`AutocompleteComp`), so the two can never disagree about what "this is issue
 * reporting, leave it alone" means. Both match it with `closest()`, so it need only
 * sit on an ANCESTOR of the real target.
 */
const ISSUE_REPORT_SELECTOR = '[data-issue-report="true"]';

/**
 * A surface that has DELIBERATELY taken the caret and must keep it, as a selector.
 *
 * Distinct from `ISSUE_REPORT_SELECTOR`, which cannot stand in for it: that marker
 * also sits on `FlagIssueButton`, which is mounted for the whole session, so its
 * presence says nothing about whether anything currently owns the caret. This one is
 * applied only by a surface while it is open, so its presence in the DOM IS the claim.
 *
 * The flag panel is `role="region"` on purpose — no MUI Modal, no backdrop, no focus
 * trap — so the `[role="dialog"]` check that covers real modals does not see it. And
 * clicking a non-focusable part of an open panel (its heading) leaves `activeElement`
 * on `<body>`, so an activeElement-only guard would conclude nothing owns the caret
 * and hand it to the omnibar, underneath an open panel. The agent then types their bug
 * report into the ORDER box, and Enter speaks it to the customer.
 *
 * So ownership is declared, not inferred. Any future surface that wants the caret
 * marks itself and `focusOmniBar` stands down without needing to know about it.
 */
const CARET_OWNER_SELECTOR = '[data-caret-owner="true"]';

const AGENT_INTERCEPTION_IGNORED_SELECTORS = [
  '[data-header="true"]',
  '[data-profile-menu="true"]',
  '[data-layout-preference="true"]',
  '[data-resize-handle]',
  '[data-transcript="true"]',
  '[data-task-router-banner="true"]',
  ISSUE_REPORT_SELECTOR,
];
const AUDIO_WS_PATH = '/audio';
const UNLEASH_POLL_INTERVAL = 15; // in seconds
const UNLEASH_APP_NAME = 'HITL';
const DUMMY_RESTAURANT_CODE = 'dummy_restaurant_code';
const A_LA_CARTE_POS_ID = 'a_la_carte_pos_id';
const TTS_PROMPT = '/tts-prompt';
const TTS_PROMPT_PREFIX = 'tts-prompt';
const UPSELL = '/upsell';
const UPSELL_PREFIX = 'upsell';
const OTEL_APP_NAME = 'hitl';
const OTEL_METRIC_HEALTH = 'hitl-health-score';
const AUTO_COMBOS = 'Auto Combos';
const UPSELL_METRIC_VERSION = '1';
const FULL_WEEKDAY_FORMAT = 'dddd';
const UPGRADE = '/upgrade';
const UPGRADE_PREFIX = 'upgrade';
const IP_CHECK_FAILED_MESSAGE =
  'Login restricted: Unauthorized network. Contact your supervisor for assistance.';
const COUPON_LENGTH = 6;
const LOYALTY_LENGTH = 10;
const AMOUNT_DEFAULTER = '--';
const COMBO_SIZE_MODIFIER_NAME_PREFIX = 'combo_size';
const ALL_ENTRIES = '**all**';
const TOTAL_PLACEHOLDER = '{total}';
const ITEM_NAME_PLACEHOLDER = '{item}';
const TOTAL_READBACK_CONSTRAINT_PREFIX = 'total_readback';
// HITL-1570: the Complete prompt to use when the donation upsell already spoke
// the check total — a completion phrase with no total in it.
const ALREADY_SPOKEN_TOTAL_READBACK_CONSTRAINT_PREFIX =
  'already_spoken_total_readback';

export {
  AGENT_INTERCEPTION_CONFIRM_CONTENT,
  AGENT_INTERCEPTION_IGNORED_SELECTORS,
  CARET_OWNER_SELECTOR,
  ISSUE_REPORT_SELECTOR,
  AUDIO_WS_PATH,
  AUTH_TOKEN_RESPONSE_HEADER,
  DUMMY_ITEM,
  DUMMY_RESTAURANT_CODE,
  FORCE_RELOAD_CHECK_TIME,
  ID,
  IDLE_TIME,
  KEY,
  MAX_CART_ITEM_QUANTITY,
  ORDER_STATUS_ICON_RESET_INTERVAL,
  OUT_OF_SESSION,
  POS_ID,
  POS_NAME,
  RESET_HEALTH_STATUS_ONCLOSE_DELAY,
  SHOW_AI_ESCALATION_INTERVAL,
  TASKROUTER_RECONNECT_ATTEMPTS,
  TASKROUTER_RECONNECT_INTERVAL,
  TASKROUTER_WSLINK,
  TIMEOUT,
  UNLEASH_APP_NAME,
  UNLEASH_POLL_INTERVAL,
  A_LA_CARTE_POS_ID,
  TTS_PROMPT,
  TTS_PROMPT_PREFIX,
  UPSELL,
  UPSELL_PREFIX,
  UPGRADE,
  UPGRADE_PREFIX,
  OTEL_APP_NAME,
  OTEL_METRIC_HEALTH,
  AUTO_COMBOS,
  UPSELL_METRIC_VERSION,
  FULL_WEEKDAY_FORMAT,
  IP_CHECK_FAILED_MESSAGE,
  COUPON_LENGTH,
  LOYALTY_LENGTH,
  AMOUNT_DEFAULTER,
  COMBO_SIZE_MODIFIER_NAME_PREFIX,
  ALL_ENTRIES,
  TOTAL_PLACEHOLDER,
  ITEM_NAME_PLACEHOLDER,
  TOTAL_READBACK_CONSTRAINT_PREFIX,
  ALREADY_SPOKEN_TOTAL_READBACK_CONSTRAINT_PREFIX,
};
